import { useApolloClient } from '@apollo/react-hooks';
import { isAppLockedQuery } from '@msales/shared/graphql/queries/task/isAppLocked';
import { getAllTasksCommentQuery } from '@msales/shared/graphql/queries/taskN/getTasksComments';
import localforage from 'localforage';
import moment from 'moment';
import { useEffect } from 'react';
import {
  useAuthContext,
  useLocalForageContext,
  useOfflineModeContext,
} from '../context';

import { notificationManager } from '../utils/notificationManager';
import { reportSyncError } from '../utils/sentryHelpers';

export const useCommentsOfflineData = () => {
  const {
    loadingComments: loading,
    setLoadingComments: setLoading,
    refetchComments: refetch,
    setRefetchComments: setRefetch,
  } = useLocalForageContext();

  const { authUser } = useAuthContext();

  const commentsStore = localforage.createInstance({
    name: 'Comments',
  });
  const { networkState } = useOfflineModeContext();
  const apolloClient = useApolloClient();

  useEffect(() => {
    const FetchProcess = async () => {
      const shouldAsync = async (refetch) => {
        try {
          const createdAt = await commentsStore.getItem('createdAt');
          if (
            (refetch ||
              !createdAt ||
              (false &&
                moment().diff(
                  moment(createdAt, 'DD/MM/YYYY HH:mm:ss'),
                  'minutes',
                ) >= 20)) &&
            (!networkState || networkState.online) &&
            authUser &&
            !loading
          ) {
            if (refetch) setRefetch(false);
            return true;
          }
          if (refetch) setRefetch(false);
          return false;
        } catch (err) {
          console.error('Error in shouldAsync:', err);
          return false;
        }
      };

      const AsyncComments = async () => {
        try {
          setLoading(true);
          notificationManager.startSync('comments', 'Komentari');
          try {
            const { data: commentsData, error: commentsError } =
              await apolloClient.query({
                query: getAllTasksCommentQuery,
                fetchPolicy: 'no-cache',
              });
            if (commentsError) {
              throw new Error(commentsError.message);
            }
            await commentsStore.setItem(
              'commentsNodes',
              commentsData.allTaskComments,
            );

            const { data: alData } = await apolloClient.query({
              query: isAppLockedQuery,
              fetchPolicy: 'no-cache',
            });
            await commentsStore.setItem('appLocked', alData?.isAppLocked);
            await commentsStore.setItem(
              'createdAt',
              moment().format('DD/MM/YYYY HH:mm:ss'),
            );

            notificationManager.completeSync('comments', true);
          } catch (err) {
            console.error('Error in AsyncComments:', err);
            reportSyncError(err, 'comments', networkState);

            let errorMessage = 'Greška prilikom učitavanja komentara!';
            if (err.networkError?.statusCode === 400) {
              errorMessage = 'Neispravni podaci za učitavanje komentara';
            } else if (err.networkError?.statusCode >= 500) {
              errorMessage = 'Greška na serveru prilikom učitavanja komentara';
            } else if (err.message?.includes('Firebase ID token')) {
              errorMessage = 'Greška autentifikacije';
            }

            notificationManager.completeSync('comments', false, errorMessage);
          }
        } finally {
          setLoading(false);
        }
      };

      const callback = async (refetch) => {
        try {
          const should = await shouldAsync(refetch);
          if (should) {
            await AsyncComments();
          }
        } catch (err) {
          console.error('Error in callback:', err);
        }
      };

      try {
        await callback(refetch);
        // setInterval(() => callback(refetch), 180000);
      } catch (err) {
        console.error('Error in FetchProcess:', err);
      }
    };

    FetchProcess();

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch, authUser]);

  return null;
};
